import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Camera, Upload, ClipboardList, Download, Plus, Trash2, Edit2, Check, X, RotateCcw, ChevronLeft, ChevronRight, AlertCircle, Loader2, Image as ImageIcon, Tag, Package, ScanBarcode, MapPin, Weight, Ruler, Search, SortAsc, SortDesc, Filter, Grid3X3, List, Calendar, ArrowUpDown, Eye, ChevronDown, History, Menu, Layers, CheckCircle2, XCircle, Video, StopCircle, SkipForward } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { cn } from '../lib/utils';
import { supabase, SUPABASE_ENABLED } from '../lib/supabaseClient';
import { SidebarTrigger } from '../components/ui/sidebar';

// ─────────────────────────────────────────────────────────────────────────────
// PALLET SCANNER MVP - Mobile-first pallet classification workflow
// ─────────────────────────────────────────────────────────────────────────────
// Flow: Scan Label → (optional) Scan Pallet → Review → Export
// Label scan extracts: shipment ID, destination, weight, sender, etc.
// Pallet scan extracts: dimensions, stacking class, condition, etc.
// AI fills in missing data with smart assumptions
// ─────────────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'capture', label: 'Scan', icon: ScanBarcode },
  { id: 'batch', label: 'Batch', icon: Layers },
  { id: 'gallery', label: 'Gallery', icon: Grid3X3 },
  { id: 'review', label: 'Review', icon: ClipboardList },
  { id: 'export', label: 'Export', icon: Download },
];

// Scan modes
const SCAN_MODES = {
  LABEL: 'label',
  PALLET: 'pallet',
};

// Capture modes for the Scan tab
const CAPTURE_MODES = {
  SINGLE: 'single',
  MULTIPLE: 'multiple',
};

// Sort options for gallery
const SORT_OPTIONS = [
  { id: 'newest', label: 'Newest first', field: 'scannedAt', dir: 'desc' },
  { id: 'oldest', label: 'Oldest first', field: 'scannedAt', dir: 'asc' },
  { id: 'name-asc', label: 'Name A-Z', field: 'name', dir: 'asc' },
  { id: 'name-desc', label: 'Name Z-A', field: 'name', dir: 'desc' },
  { id: 'weight-high', label: 'Heaviest first', field: 'weight_kg', dir: 'desc' },
  { id: 'weight-low', label: 'Lightest first', field: 'weight_kg', dir: 'asc' },
  { id: 'destination', label: 'By destination', field: 'destination', dir: 'asc' },
];

// Attribute options for pallet classification
const ATTRIBUTES = ['none', 'dangerous', 'food', 'medicine'];
const STACKING_CLASSES = ['C0', 'C1', 'C2', 'C3'];
const PALLET_TYPES = ['EUR', 'HALF', 'INDUSTRIAL', 'PLASTIC', 'OTHER'];
const CONDITIONS = ['good', 'damaged', 'severe'];
const ORIENTATIONS = ['none', 'long_side_forward', 'short_side_forward'];

// Default pallet template with label info
const createDefaultPallet = (id) => ({
  id,
  name: `Pallet ${id}`,
  imageUrl: null,
  labelImageUrl: null,
  // Label-extracted info
  shipment_id: null,
  destination: null,
  sender: null,
  receiver: null,
  // Dimensions in cm (user-friendly input)
  width_cm: 80,
  depth_cm: 120,
  height_cm: 100,
  weight_kg: 500,
  attribute: 'none',
  stacking_class: 'C1',
  notToploadable: false,
  pallet_type: 'EUR',
  condition: 'good',
  orientation_preference: 'none',
  confidence: {
    dimensions: 0.5,
    weight: 0.3,
    attribute: 0.5,
    label: 0.5,
  },
  // Source tracking
  labelScanned: false,
  palletScanned: false,
  scannedAt: new Date().toISOString(),
  isEditing: false,
});

// ─────────────────────────────────────────────────────────────────────────────
// MULTI-SCAN MODE - Continuous Label→Pallet scanning with live camera
// ─────────────────────────────────────────────────────────────────────────────
function MultiScanMode({ onPalletScanned, onExit }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  const [isActive, setIsActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [currentStep, setCurrentStep] = useState('label'); // 'label' or 'pallet'
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingLabel, setPendingLabel] = useState(null);
  const [scannedCount, setScannedCount] = useState(0);
  const [lastScanned, setLastScanned] = useState(null);
  const [processingMessage, setProcessingMessage] = useState('');

  // Start camera
  const startCamera = useCallback(async () => {
    try {
      setCameraError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setIsActive(true);
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError(err.message || 'Failed to access camera');
    }
  }, []);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsActive(false);
  }, []);

  // Start camera on mount
  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, [startCamera, stopCamera]);

  // Capture photo from video
  const capturePhoto = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current || isProcessing) return null;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    // Convert to blob
    return new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 0.9);
    });
  }, [isProcessing]);

  // Process captured image
  const processImage = useCallback(async (blob, mode) => {
    if (!blob) return null;
    
    setIsProcessing(true);
    setProcessingMessage(mode === 'label' ? 'Reading label...' : 'Analyzing pallet...');
    
    try {
      // Convert to base64
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(blob);
      });
      
      const base64DataUrl = `data:image/jpeg;base64,${base64}`;
      
      // Try storage upload
      let storageUrl = null;
      try {
        const file = new File([blob], `scan_${Date.now()}.jpg`, { type: 'image/jpeg' });
        storageUrl = await uploadImageToStorage(file);
      } catch (err) {
        console.warn('Storage upload failed:', err);
      }
      
      const imageUrl = storageUrl || base64DataUrl;
      
      // Classify based on mode
      let result;
      if (mode === 'label') {
        result = await classifyLabelImage(base64, storageUrl);
        result.labelImageUrl = imageUrl;
        result.labelScanned = true;
      } else {
        result = await classifyPalletImage(base64, storageUrl);
        result.imageUrl = imageUrl;
        result.palletScanned = true;
      }
      
      return result;
    } catch (err) {
      console.error('Processing error:', err);
      return { error: err.message };
    } finally {
      setIsProcessing(false);
      setProcessingMessage('');
    }
  }, []);

  // Handle capture button
  const handleCapture = useCallback(async () => {
    const blob = await capturePhoto();
    if (!blob) return;
    
    const result = await processImage(blob, currentStep);
    
    if (result?.error) {
      // Show error briefly, then continue
      setLastScanned({ error: result.error });
      setTimeout(() => setLastScanned(null), 3000);
      return;
    }
    
    if (currentStep === 'label') {
      // Store label data and move to pallet step
      setPendingLabel(result);
      setCurrentStep('pallet');
      setLastScanned({ type: 'label', data: result });
    } else {
      // Merge with pending label and save
      const finalPallet = pendingLabel ? {
        ...pendingLabel,
        ...result,
        shipment_id: result.shipment_id || pendingLabel.shipment_id,
        destination: result.destination || pendingLabel.destination,
        sender: result.sender || pendingLabel.sender,
        receiver: result.receiver || pendingLabel.receiver,
        weight_kg: result.weight_kg || pendingLabel.weight_kg,
        name: result.name || pendingLabel.name,
        labelImageUrl: pendingLabel.labelImageUrl,
        imageUrl: result.imageUrl,
        labelScanned: true,
        palletScanned: true,
      } : result;
      
      onPalletScanned(finalPallet);
      setScannedCount(prev => prev + 1);
      setLastScanned({ type: 'complete', data: finalPallet });
      
      // Reset for next pallet
      setPendingLabel(null);
      setCurrentStep('label');
      
      // Clear last scanned indicator after a moment
      setTimeout(() => setLastScanned(null), 2000);
    }
  }, [capturePhoto, processImage, currentStep, pendingLabel, onPalletScanned]);

  // Skip label and go directly to pallet
  const handleSkipLabel = useCallback(() => {
    setPendingLabel(null);
    setCurrentStep('pallet');
  }, []);

  // Skip pallet and save with just label data
  const handleSkipPallet = useCallback(() => {
    if (pendingLabel) {
      onPalletScanned({
        ...pendingLabel,
        palletScanned: false,
      });
      setScannedCount(prev => prev + 1);
      setLastScanned({ type: 'complete', data: pendingLabel });
      setTimeout(() => setLastScanned(null), 2000);
    }
    setPendingLabel(null);
    setCurrentStep('label');
  }, [pendingLabel, onPalletScanned]);

  // Exit multi-scan mode
  const handleExit = useCallback(() => {
    stopCamera();
    onExit();
  }, [stopCamera, onExit]);

  return (
    <div className="flex flex-col h-full bg-black">
      {/* Hidden canvas for capturing */}
      <canvas ref={canvasRef} className="hidden" />
      
      {/* Camera View */}
      <div className="flex-1 relative">
        {cameraError ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 text-white p-4">
            <AlertCircle className="w-12 h-12 mb-4 text-red-400" />
            <p className="text-center mb-4">{cameraError}</p>
            <Button onClick={startCamera} variant="outline" className="text-white border-white">
              Retry Camera
            </Button>
          </div>
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
          />
        )}
        
        {/* Processing overlay */}
        {isProcessing && (
          <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center">
            <Loader2 className="w-12 h-12 text-white animate-spin mb-4" />
            <p className="text-white text-lg">{processingMessage}</p>
          </div>
        )}
        
        {/* Top status bar */}
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/70 to-transparent p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleExit}
                className="text-white hover:bg-white/20"
              >
                <X size={20} className="mr-1" />
                Exit
              </Button>
              {scannedCount > 0 && (
                <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                  {scannedCount} scanned
                </span>
              )}
            </div>
            <div className={cn(
              "px-4 py-2 rounded-full text-sm font-bold",
              currentStep === 'label' ? "bg-blue-500 text-white" : "bg-orange-500 text-white"
            )}>
              {currentStep === 'label' ? '📋 SCAN LABEL' : '📦 SCAN PALLET'}
            </div>
          </div>
        </div>
        
        {/* Pending label indicator */}
        {pendingLabel && currentStep === 'pallet' && (
          <div className="absolute top-20 left-4 right-4">
            <div className="bg-blue-500/90 text-white rounded-lg p-3 flex items-center gap-3">
              {pendingLabel.labelImageUrl && (
                <img src={pendingLabel.labelImageUrl} alt="Label" className="w-12 h-12 object-cover rounded" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{pendingLabel.name || pendingLabel.shipment_id || 'Label scanned'}</p>
                {pendingLabel.destination && (
                  <p className="text-xs opacity-80">→ {pendingLabel.destination}</p>
                )}
              </div>
              <CheckCircle2 size={20} />
            </div>
          </div>
        )}
        
        {/* Success flash */}
        {lastScanned?.type === 'complete' && (
          <div className="absolute inset-0 bg-green-500/30 pointer-events-none animate-pulse" />
        )}
        
        {/* Error display */}
        {lastScanned?.error && (
          <div className="absolute top-20 left-4 right-4">
            <div className="bg-red-500/90 text-white rounded-lg p-3 flex items-center gap-2">
              <XCircle size={20} />
              <span className="text-sm">{lastScanned.error}</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Bottom controls */}
      <div className="bg-black p-4 pb-8">
        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <div className={cn(
            "w-3 h-3 rounded-full",
            currentStep === 'label' ? "bg-blue-500" : "bg-blue-500/40"
          )} />
          <div className="w-8 h-0.5 bg-gray-600" />
          <div className={cn(
            "w-3 h-3 rounded-full",
            currentStep === 'pallet' ? "bg-orange-500" : "bg-orange-500/40"
          )} />
        </div>
        
        <div className="flex items-center justify-center gap-4">
          {/* Skip button */}
          <Button
            variant="outline"
            size="lg"
            onClick={currentStep === 'label' ? handleSkipLabel : handleSkipPallet}
            disabled={isProcessing}
            className="h-14 px-6 text-white border-white/30 hover:bg-white/10"
          >
            <SkipForward size={20} className="mr-2" />
            Skip
          </Button>
          
          {/* Capture button */}
          <button
            onClick={handleCapture}
            disabled={isProcessing || !isActive}
            className={cn(
              "w-20 h-20 rounded-full border-4 border-white flex items-center justify-center transition-all",
              isProcessing ? "opacity-50" : "hover:scale-105 active:scale-95",
              currentStep === 'label' ? "bg-blue-500" : "bg-orange-500"
            )}
          >
            <Camera size={32} className="text-white" />
          </button>
          
          {/* Spacer for symmetry */}
          <div className="w-24" />
        </div>
        
        {/* Instructions */}
        <p className="text-center text-white/70 text-sm mt-4">
          {currentStep === 'label' 
            ? 'Point camera at shipping label and tap to capture' 
            : 'Now capture the full pallet, or skip to use AI estimates'}
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CAPTURE TAB - Two-step scanning: Label first, then Pallet
// ─────────────────────────────────────────────────────────────────────────────
function CaptureTab({ onPalletScanned, isProcessing, setIsProcessing, pendingPallet, setPendingPallet }) {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);
  const [scanMode, setScanMode] = useState(SCAN_MODES.LABEL);
  const [captureMode, setCaptureMode] = useState(CAPTURE_MODES.SINGLE);
  const [scanStep, setScanStep] = useState(1); // 1 = label, 2 = pallet (optional)

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Create preview
    const previewBlobUrl = URL.createObjectURL(file);
    setPreviewUrl(previewBlobUrl);
    setError(null);
    setIsProcessing(true);

    try {
      // Convert to base64 for fallback API and local storage
      const base64 = await fileToBase64(file);
      // Create data URL for persistent local display
      const base64DataUrl = `data:${file.type || 'image/jpeg'};base64,${base64}`;
      
      // Try to upload to storage for better Vision API results
      let storageUrl = null;
      try {
        storageUrl = await uploadImageToStorage(file);
      } catch (uploadErr) {
        console.warn('Storage upload failed, using base64 fallback:', uploadErr);
      }
      
      // Use storage URL if available, otherwise use base64 data URL (persists in memory)
      const imageUrl = storageUrl || base64DataUrl;
      console.log('[PalletScanner] scanStep:', scanStep, 'scanMode:', scanMode, 'pendingPallet:', !!pendingPallet);
      console.log('[PalletScanner] Using imageUrl:', imageUrl ? `${imageUrl.substring(0, 50)}...` : 'null');
      
      // CRITICAL: Determine scan type based on scanStep (more reliable than scanMode due to React closures)
      // Step 2 with pendingPallet means we're doing the pallet scan after label
      const shouldDoPalletScan = scanStep === 2 && pendingPallet;
      const shouldDoLabelScan = scanStep === 1 && scanMode === SCAN_MODES.LABEL;
      
      if (shouldDoPalletScan) {
        // Step 2: Scan pallet for dimensions/condition and merge with label data
        console.log('[PalletScanner] Step 2: Calling classifyPalletImage...');
        const result = await classifyPalletImage(base64, storageUrl);
        
        if (result.error) {
          setError(result.error);
        } else {
          // Merge with pending label data
          const mergedPallet = {
            ...pendingPallet,
            ...result,
            // Keep label data if pallet scan didn't provide it
            shipment_id: result.shipment_id || pendingPallet.shipment_id,
            destination: result.destination || pendingPallet.destination,
            sender: result.sender || pendingPallet.sender,
            receiver: result.receiver || pendingPallet.receiver,
            weight_kg: result.weight_kg || pendingPallet.weight_kg,
            name: result.name || pendingPallet.name,
            // Preserve both images
            labelImageUrl: pendingPallet.labelImageUrl,
            imageUrl: imageUrl,
            labelScanned: true,
            palletScanned: true,
            // Preserve other label-extracted fields
            inferred_goods_type: pendingPallet.inferred_goods_type || result.inferred_goods_type,
            carrier: pendingPallet.carrier || result.carrier,
            reference: pendingPallet.reference || result.reference,
          };
          console.log('[PalletScanner] Merged pallet with both images:', {
            labelImageUrl: mergedPallet.labelImageUrl?.substring(0, 50),
            imageUrl: mergedPallet.imageUrl?.substring(0, 50)
          });
          onPalletScanned(mergedPallet);
          setPendingPallet(null);
          setScanStep(1);
          setScanMode(SCAN_MODES.LABEL);
          setPreviewUrl(null);
        }
      } else if (shouldDoLabelScan) {
        // Step 1 Label mode: Scan label for logistics info
        console.log('[PalletScanner] Step 1: Calling classifyLabelImage...');
        const result = await classifyLabelImage(base64, storageUrl);
        
        if (result.error) {
          setError(result.error);
        } else {
          // Create pending pallet with label data
          const newPallet = {
            ...result,
            labelImageUrl: imageUrl,
            labelScanned: true,
            palletScanned: false,
          };
          console.log('[PalletScanner] Label scanned, moving to step 2. labelImageUrl:', imageUrl?.substring(0, 50));
          setPendingPallet(newPallet);
          setScanStep(2);
          setScanMode(SCAN_MODES.PALLET);
          setPreviewUrl(null);
        }
      } else {
        // Step 1 Pallet mode (user chose to scan pallet directly without label)
        console.log('[PalletScanner] Direct pallet scan (no label)...');
        const result = await classifyPalletImage(base64, storageUrl);
        
        if (result.error) {
          setError(result.error);
        } else {
          onPalletScanned({
            ...result,
            imageUrl: imageUrl,
            palletScanned: true,
            labelScanned: false,
          });
          setPreviewUrl(null);
        }
      }
    } catch (err) {
      console.error('[PalletScanner] Error:', err);
      setError(err.message || 'Failed to process image');
    } finally {
      setIsProcessing(false);
    }
  }, [onPalletScanned, setIsProcessing, scanMode, scanStep, pendingPallet, setPendingPallet]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, [handleFile]);

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const clearPreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setError(null);
  };

  const skipPalletScan = () => {
    if (pendingPallet) {
      // Use AI assumptions for dimensions
      onPalletScanned({
        ...pendingPallet,
        palletScanned: false,
        // AI will have already filled in assumed values
      });
      setPendingPallet(null);
      setScanStep(1);
      setScanMode(SCAN_MODES.LABEL);
    }
  };

  const cancelPending = () => {
    setPendingPallet(null);
    setScanStep(1);
    setScanMode(SCAN_MODES.LABEL);
    setPreviewUrl(null);
    setError(null);
  };

  // If in multi-scan mode, render that instead
  if (captureMode === CAPTURE_MODES.MULTIPLE) {
    return (
      <MultiScanMode
        onPalletScanned={onPalletScanned}
        onExit={() => setCaptureMode(CAPTURE_MODES.SINGLE)}
      />
    );
  }

  return (
    <div className="flex flex-col items-center gap-4 p-4">
      {/* Mode selector: Single vs Multiple */}
      <div className="flex gap-2 w-full max-w-md">
        <button
          onClick={() => setCaptureMode(CAPTURE_MODES.SINGLE)}
          className={cn(
            "flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg border-2 transition-all text-sm",
            captureMode === CAPTURE_MODES.SINGLE
              ? "border-blue-600 bg-blue-50 text-blue-700"
              : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
          )}
        >
          <Camera size={16} />
          <span className="font-medium">Single Scan</span>
        </button>
        <button
          onClick={() => setCaptureMode(CAPTURE_MODES.MULTIPLE)}
          className={cn(
            "flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg border-2 transition-all text-sm",
            captureMode === CAPTURE_MODES.MULTIPLE
              ? "border-green-600 bg-green-50 text-green-700"
              : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
          )}
        >
          <Video size={16} />
          <span className="font-medium">Scan Multiple</span>
        </button>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2 w-full max-w-md mb-2">
        <div className={cn(
          "flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium",
          scanStep >= 1 ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-500"
        )}>
          1
        </div>
        <div className={cn(
          "flex-1 h-1 rounded",
          scanStep >= 2 ? "bg-blue-600" : "bg-gray-200"
        )} />
        <div className={cn(
          "flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium",
          scanStep >= 2 ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-500"
        )}>
          2
        </div>
      </div>
      
      {/* Step labels */}
      <div className="flex justify-between w-full max-w-md text-xs text-gray-500 -mt-2 mb-2">
        <span className={scanStep === 1 ? "text-blue-600 font-medium" : ""}>Scan Label</span>
        <span className={scanStep === 2 ? "text-blue-600 font-medium" : ""}>Scan Pallet (optional)</span>
      </div>

      {/* Pending pallet info card */}
      {pendingPallet && scanStep === 2 && (
        <Card className="w-full max-w-md bg-blue-50 border-blue-200">
          <CardContent className="p-3">
            <div className="flex items-start gap-3">
              {pendingPallet.labelImageUrl && (
                <img
                  src={pendingPallet.labelImageUrl}
                  alt="Label"
                  className="w-20 h-20 object-cover rounded border"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1 mb-1">
                  <CheckCircle2 size={14} className="text-green-600" />
                  <span className="text-xs font-medium text-green-700">Label scanned</span>
                </div>
                <h4 className="font-medium text-sm truncate">{pendingPallet.name || 'Scanned Pallet'}</h4>
                {pendingPallet.shipment_id && (
                  <p className="text-xs text-gray-600 flex items-center gap-1">
                    <Tag size={12} /> {pendingPallet.shipment_id}
                  </p>
                )}
                {pendingPallet.destination && (
                  <p className="text-xs text-gray-600 flex items-center gap-1">
                    <MapPin size={12} /> {pendingPallet.destination}
                  </p>
                )}
                <div className="flex gap-2 mt-1">
                  {pendingPallet.weight_kg && (
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Weight size={10} /> {pendingPallet.weight_kg}kg
                    </p>
                  )}
                  {pendingPallet.width_cm && (
                    <p className="text-xs text-gray-500">
                      {pendingPallet.width_cm}×{pendingPallet.depth_cm}×{pendingPallet.height_cm}cm
                    </p>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={cancelPending}
                className="text-gray-500 -mr-2"
              >
                <X size={16} />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scan mode selector (only show on step 1) */}
      {scanStep === 1 && (
        <div className="flex gap-2 w-full max-w-md">
          <button
            onClick={() => setScanMode(SCAN_MODES.LABEL)}
            className={cn(
              "flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 transition-all",
              scanMode === SCAN_MODES.LABEL
                ? "border-blue-600 bg-blue-50 text-blue-700"
                : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
            )}
          >
            <Tag size={18} />
            <span className="text-sm font-medium">Label</span>
          </button>
          <button
            onClick={() => setScanMode(SCAN_MODES.PALLET)}
            className={cn(
              "flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 transition-all",
              scanMode === SCAN_MODES.PALLET
                ? "border-blue-600 bg-blue-50 text-blue-700"
                : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
            )}
          >
            <Package size={18} />
            <span className="text-sm font-medium">Full Pallet</span>
          </button>
        </div>
      )}

      {/* Instructions */}
      <div className="text-center text-sm text-gray-600 w-full max-w-md">
        {scanStep === 1 && scanMode === SCAN_MODES.LABEL && (
          <p>📋 Scan the shipping label to extract ID, destination, weight, etc.</p>
        )}
        {scanStep === 1 && scanMode === SCAN_MODES.PALLET && (
          <p>📦 Scan the full pallet to get dimensions and condition.</p>
        )}
        {scanStep === 2 && (
          <p>📦 Now scan the full pallet for dimensions, or skip to use AI estimates.</p>
        )}
      </div>

      {/* Camera/Upload Zone */}
      <div
        className={cn(
          "relative w-full max-w-md aspect-[4/3] rounded-xl border-2 border-dashed transition-all duration-200",
          dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 bg-gray-50",
          isProcessing && "opacity-50 pointer-events-none"
        )}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {previewUrl ? (
          <div className="relative w-full h-full">
            <img
              src={previewUrl}
              alt="Preview"
              className="w-full h-full object-contain rounded-lg"
            />
            {!isProcessing && (
              <button
                onClick={clearPreview}
                className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
              >
                <X size={16} />
              </button>
            )}
            {isProcessing && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
                <div className="flex flex-col items-center gap-2 text-white">
                  <Loader2 className="w-8 h-8 animate-spin" />
                  <span className="text-sm font-medium">
                    {scanMode === SCAN_MODES.LABEL ? 'Reading label...' : 'Analyzing pallet...'}
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6">
            <div className="p-4 bg-white rounded-full shadow-sm">
              {scanMode === SCAN_MODES.LABEL || scanStep === 2 ? (
                scanStep === 2 ? <Package className="w-8 h-8 text-gray-400" /> : <Tag className="w-8 h-8 text-gray-400" />
              ) : (
                <Package className="w-8 h-8 text-gray-400" />
              )}
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-700">
                {scanStep === 2 ? 'Drop pallet image or use buttons' : 
                 scanMode === SCAN_MODES.LABEL ? 'Drop label image or use buttons' : 'Drop pallet image or use buttons'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Supports JPG, PNG, WebP
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 w-full max-w-md">
        <Button
          onClick={() => cameraInputRef.current?.click()}
          disabled={isProcessing}
          className="flex-1 h-14 text-base gap-2"
          variant="default"
        >
          <Camera size={20} />
          Camera
        </Button>
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={isProcessing}
          className="flex-1 h-14 text-base gap-2"
          variant="outline"
        >
          <Upload size={20} />
          Upload
        </Button>
      </div>

      {/* Pallet scan options (step 2) */}
      {scanStep === 2 && pendingPallet && (
        <div className="w-full max-w-md space-y-2">
          <Button
            onClick={skipPalletScan}
            variant="default"
            className="w-full"
            disabled={isProcessing}
          >
            <Check size={16} className="mr-2" />
            Save with AI Dimensions
          </Button>
          <p className="text-xs text-center text-gray-500">
            Or scan the pallet above for more accurate dimensions
          </p>
        </div>
      )}

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInput}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileInput}
        className="hidden"
      />

      {/* Error display */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg w-full max-w-md">
          <AlertCircle size={18} />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Quick Add Manual Button */}
      {scanStep === 1 && (
        <Button
          onClick={() => onPalletScanned(null)}
          variant="ghost"
          className="text-gray-500"
        >
          <Plus size={16} className="mr-1" />
          Add manually without scanning
        </Button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BATCH SCAN TAB - Upload multiple images at once for batch classification
// ─────────────────────────────────────────────────────────────────────────────
function BatchScanTab({ onBatchComplete, isProcessing, setIsProcessing }) {
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]); // Array of { file, preview, status, result, error }
  const [dragActive, setDragActive] = useState(false);
  const [scanMode, setScanMode] = useState(SCAN_MODES.LABEL);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [totalProcessed, setTotalProcessed] = useState(0);

  // Handle file selection
  const handleFiles = useCallback((files) => {
    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    
    if (imageFiles.length === 0) return;
    
    const newFiles = imageFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      status: 'pending', // pending, processing, success, error
      result: null,
      error: null,
    }));
    
    setSelectedFiles(prev => [...prev, ...newFiles]);
  }, []);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
    e.target.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => {
      const newFiles = [...prev];
      if (newFiles[index].preview) {
        URL.revokeObjectURL(newFiles[index].preview);
      }
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const clearAll = () => {
    selectedFiles.forEach(f => {
      if (f.preview) URL.revokeObjectURL(f.preview);
    });
    setSelectedFiles([]);
    setCurrentIndex(-1);
    setTotalProcessed(0);
  };

  // Process all selected images
  const processAll = async () => {
    if (selectedFiles.length === 0 || isProcessing) return;
    
    setIsProcessing(true);
    setTotalProcessed(0);
    const results = [];
    
    for (let i = 0; i < selectedFiles.length; i++) {
      const fileObj = selectedFiles[i];
      if (fileObj.status === 'success') {
        // Already processed, skip
        results.push(fileObj.result);
        continue;
      }
      
      setCurrentIndex(i);
      
      // Update status to processing
      setSelectedFiles(prev => prev.map((f, idx) => 
        idx === i ? { ...f, status: 'processing' } : f
      ));
      
      try {
        // Convert to base64
        const base64 = await fileToBase64(fileObj.file);
        const base64DataUrl = `data:${fileObj.file.type || 'image/jpeg'};base64,${base64}`;
        
        // Try to upload to storage
        let storageUrl = null;
        try {
          storageUrl = await uploadImageToStorage(fileObj.file);
        } catch (uploadErr) {
          console.warn('Storage upload failed for batch item:', uploadErr);
        }
        
        const imageUrl = storageUrl || base64DataUrl;
        
        // Classify based on mode
        let result;
        if (scanMode === SCAN_MODES.LABEL) {
          result = await classifyLabelImage(base64, storageUrl);
        } else {
          result = await classifyPalletImage(base64, storageUrl);
        }
        
        // Add image URL to result
        if (scanMode === SCAN_MODES.LABEL) {
          result.labelImageUrl = imageUrl;
          result.labelScanned = true;
        } else {
          result.imageUrl = imageUrl;
          result.palletScanned = true;
        }
        
        results.push(result);
        
        // Update status to success
        setSelectedFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, status: 'success', result } : f
        ));
        
      } catch (err) {
        console.error('Batch processing error for file:', fileObj.file.name, err);
        
        // Update status to error
        setSelectedFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, status: 'error', error: err.message } : f
        ));
      }
      
      setTotalProcessed(i + 1);
      
      // Small delay between API calls to avoid rate limiting
      if (i < selectedFiles.length - 1) {
        await new Promise(r => setTimeout(r, 500));
      }
    }
    
    setCurrentIndex(-1);
    setIsProcessing(false);
    
    // Call completion callback with successful results
    if (results.length > 0) {
      onBatchComplete(results);
    }
  };

  // Get stats
  const stats = useMemo(() => {
    const pending = selectedFiles.filter(f => f.status === 'pending').length;
    const processing = selectedFiles.filter(f => f.status === 'processing').length;
    const success = selectedFiles.filter(f => f.status === 'success').length;
    const error = selectedFiles.filter(f => f.status === 'error').length;
    return { pending, processing, success, error, total: selectedFiles.length };
  }, [selectedFiles]);

  return (
    <div className="flex flex-col gap-4 p-4 max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-lg font-semibold text-gray-900">Batch Scan</h2>
        <p className="text-sm text-gray-600">Upload multiple images to classify them all at once</p>
      </div>

      {/* Scan Mode Selector */}
      <div className="flex gap-2 w-full max-w-md mx-auto">
        <button
          onClick={() => setScanMode(SCAN_MODES.LABEL)}
          disabled={isProcessing}
          className={cn(
            "flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 transition-all",
            scanMode === SCAN_MODES.LABEL
              ? "border-blue-600 bg-blue-50 text-blue-700"
              : "border-gray-200 bg-white text-gray-600 hover:border-gray-300",
            isProcessing && "opacity-50 cursor-not-allowed"
          )}
        >
          <Tag size={18} />
          <span className="text-sm font-medium">Labels</span>
        </button>
        <button
          onClick={() => setScanMode(SCAN_MODES.PALLET)}
          disabled={isProcessing}
          className={cn(
            "flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 transition-all",
            scanMode === SCAN_MODES.PALLET
              ? "border-blue-600 bg-blue-50 text-blue-700"
              : "border-gray-200 bg-white text-gray-600 hover:border-gray-300",
            isProcessing && "opacity-50 cursor-not-allowed"
          )}
        >
          <Package size={18} />
          <span className="text-sm font-medium">Pallets</span>
        </button>
      </div>

      {/* Drop Zone */}
      {!isProcessing && (
        <div
          className={cn(
            "relative w-full max-w-md mx-auto aspect-[3/2] rounded-xl border-2 border-dashed transition-all duration-200 cursor-pointer",
            dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 bg-gray-50 hover:border-gray-400"
          )}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4">
            <div className="p-4 bg-white rounded-full shadow-sm">
              <Layers className="w-8 h-8 text-gray-400" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-700">
                Drop multiple images here
              </p>
              <p className="text-xs text-gray-500 mt-1">
                or click to select files
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileInput}
        className="hidden"
      />

      {/* Selected Files Grid */}
      {selectedFiles.length > 0 && (
        <div className="w-full max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-700">
              Selected Images ({selectedFiles.length})
            </h3>
            {!isProcessing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAll}
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 size={14} className="mr-1" />
                Clear All
              </Button>
            )}
          </div>
          
          {/* Progress bar during processing */}
          {isProcessing && (
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                <span>Processing... ({totalProcessed}/{selectedFiles.length})</span>
                <span>{Math.round((totalProcessed / selectedFiles.length) * 100)}%</span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-600 transition-all duration-300"
                  style={{ width: `${(totalProcessed / selectedFiles.length) * 100}%` }}
                />
              </div>
            </div>
          )}
          
          {/* Stats */}
          {(stats.success > 0 || stats.error > 0) && !isProcessing && (
            <div className="flex gap-4 mb-4 text-sm">
              {stats.success > 0 && (
                <span className="flex items-center gap-1 text-green-600">
                  <CheckCircle2 size={14} />
                  {stats.success} successful
                </span>
              )}
              {stats.error > 0 && (
                <span className="flex items-center gap-1 text-red-600">
                  <XCircle size={14} />
                  {stats.error} failed
                </span>
              )}
              {stats.pending > 0 && (
                <span className="text-gray-500">
                  {stats.pending} pending
                </span>
              )}
            </div>
          )}
          
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
            {selectedFiles.map((fileObj, index) => (
              <div
                key={index}
                className={cn(
                  "relative aspect-square rounded-lg overflow-hidden border-2",
                  fileObj.status === 'pending' && "border-gray-200",
                  fileObj.status === 'processing' && "border-blue-400",
                  fileObj.status === 'success' && "border-green-400",
                  fileObj.status === 'error' && "border-red-400"
                )}
              >
                <img
                  src={fileObj.preview}
                  alt={`File ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                
                {/* Status overlay */}
                {fileObj.status === 'processing' && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  </div>
                )}
                {fileObj.status === 'success' && (
                  <div className="absolute top-1 right-1">
                    <CheckCircle2 className="w-5 h-5 text-green-500 bg-white rounded-full" />
                  </div>
                )}
                {fileObj.status === 'error' && (
                  <div className="absolute inset-0 bg-red-500/30 flex items-center justify-center">
                    <XCircle className="w-6 h-6 text-red-600 bg-white rounded-full" />
                  </div>
                )}
                
                {/* Remove button (only when not processing) */}
                {!isProcessing && fileObj.status !== 'processing' && (
                  <button
                    onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                    className="absolute top-1 left-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col gap-2 w-full max-w-md mx-auto">
        {selectedFiles.length > 0 && !isProcessing && (
          <Button
            onClick={processAll}
            disabled={stats.pending === 0 && stats.error === 0}
            className="w-full h-12 text-base gap-2"
          >
            <ScanBarcode size={20} />
            {stats.success > 0 && stats.pending === 0 
              ? `Done! View ${stats.success} pallets in Review`
              : `Classify ${stats.pending + stats.error} ${scanMode === SCAN_MODES.LABEL ? 'Labels' : 'Pallets'}`
            }
          </Button>
        )}
        
        {!isProcessing && selectedFiles.length === 0 && (
          <Button
            onClick={() => fileInputRef.current?.click()}
            className="w-full h-12 text-base gap-2"
            variant="outline"
          >
            <Upload size={20} />
            Select Images
          </Button>
        )}

        {isProcessing && (
          <Button
            disabled
            className="w-full h-12 text-base gap-2"
          >
            <Loader2 size={20} className="animate-spin" />
            Processing {currentIndex + 1} of {selectedFiles.length}...
          </Button>
        )}
      </div>

      {/* Tips */}
      <div className="text-center text-xs text-gray-500 max-w-md mx-auto">
        <p>💡 Tip: For best results, ensure images are clear and well-lit</p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GALLERY TAB - Visual grid of all scanned pallets with search & sort
// ─────────────────────────────────────────────────────────────────────────────
function GalleryTab({ pallets, onSelect, onDelete }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [filterAttribute, setFilterAttribute] = useState('all');
  const [filterDestination, setFilterDestination] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPallet, setSelectedPallet] = useState(null);

  // Get unique destinations for filter
  const destinations = useMemo(() => {
    const dests = [...new Set(pallets.map(p => p.destination).filter(Boolean))];
    return dests.sort();
  }, [pallets]);

  // Filter and sort pallets
  const filteredPallets = useMemo(() => {
    let result = [...pallets];
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p => 
        (p.name || '').toLowerCase().includes(query) ||
        (p.shipment_id || '').toLowerCase().includes(query) ||
        (p.destination || '').toLowerCase().includes(query) ||
        (p.sender || '').toLowerCase().includes(query) ||
        (p.receiver || '').toLowerCase().includes(query)
      );
    }
    
    // Attribute filter
    if (filterAttribute !== 'all') {
      result = result.filter(p => p.attribute === filterAttribute);
    }
    
    // Destination filter
    if (filterDestination !== 'all') {
      result = result.filter(p => p.destination === filterDestination);
    }
    
    // Sort
    const sortOption = SORT_OPTIONS.find(s => s.id === sortBy) || SORT_OPTIONS[0];
    result.sort((a, b) => {
      let aVal = a[sortOption.field];
      let bVal = b[sortOption.field];
      
      // Handle null/undefined
      if (aVal == null) aVal = sortOption.dir === 'asc' ? '\uffff' : '';
      if (bVal == null) bVal = sortOption.dir === 'asc' ? '\uffff' : '';
      
      // Compare
      if (typeof aVal === 'string') {
        const cmp = aVal.localeCompare(bVal);
        return sortOption.dir === 'asc' ? cmp : -cmp;
      } else {
        const cmp = (aVal || 0) - (bVal || 0);
        return sortOption.dir === 'asc' ? cmp : -cmp;
      }
    });
    
    return result;
  }, [pallets, searchQuery, sortBy, filterAttribute, filterDestination]);

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Attribute badge colors
  const getAttributeBadge = (attr) => {
    switch (attr) {
      case 'dangerous': return 'bg-red-100 text-red-700 border-red-200';
      case 'food': return 'bg-green-100 text-green-700 border-green-200';
      case 'medicine': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  // Condition badge
  const getConditionBadge = (condition) => {
    switch (condition) {
      case 'damaged': return 'bg-orange-100 text-orange-700';
      case 'severe': return 'bg-red-100 text-red-700';
      default: return 'bg-green-100 text-green-700';
    }
  };

  if (pallets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="p-4 bg-gray-100 rounded-full mb-4">
          <Grid3X3 className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-700 mb-2">No pallets scanned yet</h3>
        <p className="text-sm text-gray-500 text-center">
          Use the Scan tab to capture pallet images
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Search & Controls Header */}
      <div className="sticky top-0 bg-white border-b z-10 p-3 space-y-3">
        {/* Search bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, ID, destination..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Controls row */}
        <div className="flex items-center gap-2">
          {/* Sort dropdown */}
          <div className="relative flex-1">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full appearance-none pl-3 pr-8 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {SORT_OPTIONS.map(opt => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
            <ArrowUpDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Filter toggle */}
          <Button
            variant={showFilters ? "default" : "outline"}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="gap-1"
          >
            <Filter size={16} />
            <span className="hidden sm:inline">Filter</span>
            {(filterAttribute !== 'all' || filterDestination !== 'all') && (
              <span className="ml-1 px-1.5 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                {(filterAttribute !== 'all' ? 1 : 0) + (filterDestination !== 'all' ? 1 : 0)}
              </span>
            )}
          </Button>

          {/* View mode toggle */}
          <div className="flex border rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={cn(
                "p-2 transition-colors",
                viewMode === 'grid' ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
              )}
            >
              <Grid3X3 size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                "p-2 transition-colors",
                viewMode === 'list' ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
              )}
            >
              <List size={16} />
            </button>
          </div>
        </div>

        {/* Filter options (collapsible) */}
        {showFilters && (
          <div className="flex flex-wrap gap-2 pt-2 border-t">
            <div className="flex-1 min-w-[140px]">
              <label className="text-xs text-gray-500 mb-1 block">Attribute</label>
              <select
                value={filterAttribute}
                onChange={(e) => setFilterAttribute(e.target.value)}
                className="w-full px-2 py-1.5 border rounded text-sm"
              >
                <option value="all">All types</option>
                {ATTRIBUTES.map(a => (
                  <option key={a} value={a}>{a === 'none' ? 'Standard' : a.charAt(0).toUpperCase() + a.slice(1)}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className="text-xs text-gray-500 mb-1 block">Destination</label>
              <select
                value={filterDestination}
                onChange={(e) => setFilterDestination(e.target.value)}
                className="w-full px-2 py-1.5 border rounded text-sm"
              >
                <option value="all">All destinations</option>
                {destinations.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            {(filterAttribute !== 'all' || filterDestination !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setFilterAttribute('all'); setFilterDestination('all'); }}
                className="self-end text-gray-500"
              >
                Clear filters
              </Button>
            )}
          </div>
        )}

        {/* Results count */}
        <div className="text-xs text-gray-500">
          {filteredPallets.length} of {pallets.length} pallets
          {searchQuery && ` matching "${searchQuery}"`}
        </div>
      </div>

      {/* Pallet Grid/List */}
      <div className="flex-1 overflow-y-auto p-3">
        {filteredPallets.length === 0 ? (
          <div className="text-center py-12">
            <Search className="w-8 h-8 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No pallets match your search</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSearchQuery(''); setFilterAttribute('all'); setFilterDestination('all'); }}
              className="mt-2"
            >
              Clear all filters
            </Button>
          </div>
        ) : viewMode === 'grid' ? (
          // Grid view
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {filteredPallets.map((pallet) => {
              const imgSrc = pallet.imageUrl || pallet.labelImageUrl;
              return (
              <div
                key={pallet.id}
                onClick={() => setSelectedPallet(pallet)}
                className="group cursor-pointer bg-white rounded-lg border shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              >
                {/* Image */}
                <div className="aspect-square bg-gray-100 relative">
                  {imgSrc ? (
                    <img
                      src={imgSrc}
                      alt={pallet.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.error('[PalletScanner] Image load error:', imgSrc?.substring(0, 100));
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-8 h-8 text-gray-300" />
                    </div>
                  )}
                  {/* Overlay badges */}
                  <div className="absolute top-1.5 left-1.5 flex flex-col gap-1">
                    {pallet.attribute && pallet.attribute !== 'none' && (
                      <span className={cn("px-1.5 py-0.5 rounded text-xs font-medium border", getAttributeBadge(pallet.attribute))}>
                        {pallet.attribute}
                      </span>
                    )}
                    {pallet.notToploadable && (
                      <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">
                        No stack
                      </span>
                    )}
                  </div>
                  {/* Condition indicator */}
                  <div className="absolute top-1.5 right-1.5">
                    <span className={cn("px-1.5 py-0.5 rounded text-xs font-medium", getConditionBadge(pallet.condition))}>
                      {pallet.condition === 'good' ? 'OK' : pallet.condition}
                    </span>
                  </div>
                  {/* View overlay on hover */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
                
                {/* Info */}
                <div className="p-2.5 space-y-1.5">
                  <h4 className="font-medium text-sm truncate">{pallet.name}</h4>
                  
                  {/* Destination - prominent */}
                  {pallet.destination && (
                    <div className="flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                      <MapPin size={11} className="flex-shrink-0" />
                      <span className="truncate">{pallet.destination}</span>
                    </div>
                  )}
                  
                  {/* Dimensions */}
                  <div className="flex items-center gap-1 text-xs text-gray-600">
                    <Ruler size={11} className="flex-shrink-0 text-gray-400" />
                    <span>{pallet.width_cm}×{pallet.depth_cm}×{pallet.height_cm} cm</span>
                  </div>
                  
                  {/* Weight */}
                  <div className="flex items-center gap-1 text-xs text-gray-600">
                    <Weight size={11} className="flex-shrink-0 text-gray-400" />
                    <span>{pallet.weight_kg} kg</span>
                  </div>
                  
                  {/* Shipment ID and time */}
                  <div className="flex items-center justify-between pt-1 border-t border-gray-100 text-xs text-gray-400">
                    {pallet.shipment_id ? (
                      <span className="truncate">{pallet.shipment_id}</span>
                    ) : (
                      <span>—</span>
                    )}
                    <span className="flex-shrink-0">{formatDate(pallet.scannedAt)}</span>
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        ) : (
          // List view
          <div className="space-y-2">
            {filteredPallets.map((pallet) => (
              <div
                key={pallet.id}
                onClick={() => setSelectedPallet(pallet)}
                className="flex items-center gap-3 p-3 bg-white rounded-lg border shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              >
                {/* Thumbnail */}
                <div className="w-16 h-16 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden">
                  {pallet.imageUrl || pallet.labelImageUrl ? (
                    <img
                      src={pallet.imageUrl || pallet.labelImageUrl}
                      alt={pallet.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-6 h-6 text-gray-300" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h4 className="font-medium text-sm truncate">{pallet.name}</h4>
                      {pallet.shipment_id && (
                        <p className="text-xs text-blue-600">{pallet.shipment_id}</p>
                      )}
                    </div>
                    <span className={cn("px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0", getConditionBadge(pallet.condition))}>
                      {pallet.condition === 'good' ? 'OK' : pallet.condition}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                    <span>{pallet.width_cm}×{pallet.depth_cm}×{pallet.height_cm} cm</span>
                    <span>{pallet.weight_kg} kg</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {pallet.destination && (
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <MapPin size={10} /> {pallet.destination}
                      </span>
                    )}
                    {pallet.attribute && pallet.attribute !== 'none' && (
                      <span className={cn("px-1.5 py-0.5 rounded text-xs font-medium border", getAttributeBadge(pallet.attribute))}>
                        {pallet.attribute}
                      </span>
                    )}
                    {pallet.notToploadable && (
                      <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">
                        No stack
                      </span>
                    )}
                  </div>
                </div>

                {/* Time */}
                <div className="text-xs text-gray-400 text-right flex-shrink-0">
                  {formatDate(pallet.scannedAt)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pallet Detail Modal */}
      {selectedPallet && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPallet(null)}
        >
          <div 
            className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <h3 className="font-semibold text-lg">{selectedPallet.name}</h3>
              <button
                onClick={() => setSelectedPallet(null)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-4">
              {/* Image */}
              {(selectedPallet.imageUrl || selectedPallet.labelImageUrl) && (
                <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={selectedPallet.imageUrl || selectedPallet.labelImageUrl}
                    alt={selectedPallet.name}
                    className="w-full h-full object-contain"
                  />
                </div>
              )}

              {/* Status badges */}
              <div className="flex flex-wrap gap-2">
                <span className={cn("px-2 py-1 rounded-full text-xs font-medium", getConditionBadge(selectedPallet.condition))}>
                  Condition: {selectedPallet.condition}
                </span>
                {selectedPallet.attribute && selectedPallet.attribute !== 'none' && (
                  <span className={cn("px-2 py-1 rounded-full text-xs font-medium border", getAttributeBadge(selectedPallet.attribute))}>
                    {selectedPallet.attribute}
                  </span>
                )}
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  {selectedPallet.pallet_type}
                </span>
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  Stack: {selectedPallet.stacking_class}
                </span>
                {selectedPallet.notToploadable && (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                    Not stackable
                  </span>
                )}
              </div>

              {/* Details grid */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                {selectedPallet.shipment_id && (
                  <div>
                    <label className="text-xs text-gray-500">Shipment ID</label>
                    <p className="font-medium text-blue-600">{selectedPallet.shipment_id}</p>
                  </div>
                )}
                {selectedPallet.destination && (
                  <div>
                    <label className="text-xs text-gray-500">Destination</label>
                    <p className="font-medium">{selectedPallet.destination}</p>
                  </div>
                )}
                {selectedPallet.sender && (
                  <div>
                    <label className="text-xs text-gray-500">Sender</label>
                    <p className="font-medium">{selectedPallet.sender}</p>
                  </div>
                )}
                {selectedPallet.receiver && (
                  <div>
                    <label className="text-xs text-gray-500">Receiver</label>
                    <p className="font-medium">{selectedPallet.receiver}</p>
                  </div>
                )}
                <div>
                  <label className="text-xs text-gray-500">Dimensions</label>
                  <p className="font-medium">{selectedPallet.width_cm}×{selectedPallet.depth_cm}×{selectedPallet.height_cm} cm</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Weight</label>
                  <p className="font-medium">{selectedPallet.weight_kg} kg</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Scanned</label>
                  <p className="font-medium">{new Date(selectedPallet.scannedAt).toLocaleString()}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Scan type</label>
                  <p className="font-medium">
                    {selectedPallet.labelScanned && selectedPallet.palletScanned ? 'Label + Pallet' :
                     selectedPallet.labelScanned ? 'Label only' :
                     selectedPallet.palletScanned ? 'Pallet only' : 'Manual'}
                  </p>
                </div>
              </div>

              {/* Label image if separate */}
              {selectedPallet.labelImageUrl && selectedPallet.imageUrl && selectedPallet.labelImageUrl !== selectedPallet.imageUrl && (
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Label image</label>
                  <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden max-w-[200px]">
                    <img
                      src={selectedPallet.labelImageUrl}
                      alt="Label"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-white border-t p-4 flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  onSelect(selectedPallet);
                  setSelectedPallet(null);
                }}
              >
                <Edit2 size={16} className="mr-1" />
                Edit
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  onDelete(selectedPallet.id);
                  setSelectedPallet(null);
                }}
              >
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// REVIEW TAB - Editable Pallet Table
// ─────────────────────────────────────────────────────────────────────────────
function ReviewTab({ pallets, onUpdate, onDelete }) {
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const startEdit = (pallet) => {
    setEditingId(pallet.id);
    setEditForm({ ...pallet });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const saveEdit = () => {
    if (editForm) {
      onUpdate(editForm);
    }
    setEditingId(null);
    setEditForm(null);
  };

  const updateField = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  if (pallets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="p-4 bg-gray-100 rounded-full mb-4">
          <ClipboardList className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-700 mb-2">No pallets yet</h3>
        <p className="text-sm text-gray-500 text-center">
          Use the Capture tab to scan or add pallets
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      {/* Stats bar */}
      <div className="flex items-center justify-between px-2">
        <span className="text-sm text-gray-600">
          {pallets.length} pallet{pallets.length !== 1 ? 's' : ''} scanned
        </span>
        <span className="text-xs text-gray-500">
          Tap to edit
        </span>
      </div>

      {/* Pallet cards */}
      <div className="space-y-3">
        {pallets.map((pallet) => (
          <Card key={pallet.id} className="overflow-hidden">
            {editingId === pallet.id ? (
              // Edit mode
              <div className="p-4 space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium">Edit Pallet</h4>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={cancelEdit}>
                      <X size={16} />
                    </Button>
                    <Button size="sm" onClick={saveEdit}>
                      <Check size={16} />
                    </Button>
                  </div>
                </div>
                
                {/* Name */}
                <div>
                  <label className="text-xs text-gray-500">Name / Description</label>
                  <input
                    type="text"
                    value={editForm?.name || ''}
                    onChange={(e) => updateField('name', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>

                {/* Scanned Images Preview */}
                {(editForm?.labelImageUrl || editForm?.imageUrl) && (
                  <div className="flex gap-2 border rounded-lg p-2 bg-gray-50">
                    {editForm?.labelImageUrl && (
                      <div className="flex-1">
                        <p className="text-[10px] text-gray-500 mb-1 flex items-center gap-1">
                          <Tag size={10} /> Label Scan
                        </p>
                        <img
                          src={editForm.labelImageUrl}
                          alt="Label"
                          className="w-full h-24 object-cover rounded border"
                        />
                      </div>
                    )}
                    {editForm?.imageUrl && (
                      <div className="flex-1">
                        <p className="text-[10px] text-gray-500 mb-1 flex items-center gap-1">
                          <Package size={10} /> Pallet Scan
                        </p>
                        <img
                          src={editForm.imageUrl}
                          alt="Pallet"
                          className="w-full h-24 object-cover rounded border"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Label Info Section */}
                <div className="border-t pt-3 mt-2">
                  <p className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                    <Tag size={12} /> Shipping Info
                  </p>
                  
                  {/* Inferred goods type - important for stacking */}
                  {editForm?.inferred_goods_type && (
                    <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                      <span className="font-medium text-yellow-800">🔍 AI detected: </span>
                      <span className="text-yellow-700">{editForm.inferred_goods_type}</span>
                    </div>
                  )}
                  
                  {/* Carrier info if available */}
                  {editForm?.carrier && (
                    <div className="mb-2 text-xs text-gray-600">
                      <span className="font-medium">Carrier:</span> {editForm.carrier}
                      {editForm.terminal_code && <span className="ml-2 text-gray-500">({editForm.terminal_code})</span>}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-500">Shipment ID</label>
                      <input
                        type="text"
                        value={editForm?.shipment_id || ''}
                        onChange={(e) => updateField('shipment_id', e.target.value)}
                        placeholder="e.g. SHP-12345"
                        className="w-full px-2 py-1.5 border rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Destination</label>
                      <input
                        type="text"
                        value={editForm?.destination || ''}
                        onChange={(e) => updateField('destination', e.target.value)}
                        placeholder="City or address"
                        className="w-full px-2 py-1.5 border rounded text-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <label className="text-xs text-gray-500">Sender</label>
                      <input
                        type="text"
                        value={editForm?.sender || ''}
                        onChange={(e) => updateField('sender', e.target.value)}
                        placeholder="Company name"
                        className="w-full px-2 py-1.5 border rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Receiver</label>
                      <input
                        type="text"
                        value={editForm?.receiver || ''}
                        onChange={(e) => updateField('receiver', e.target.value)}
                        placeholder="Company name"
                        className="w-full px-2 py-1.5 border rounded text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Dimensions Grid */}
                <div className="border-t pt-3 mt-2">
                  <p className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                    <Ruler size={12} /> Dimensions
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-gray-500">Width (cm)</label>
                      <input
                        type="number"
                        value={editForm?.width_cm || ''}
                        onChange={(e) => updateField('width_cm', parseFloat(e.target.value) || 0)}
                        className="w-full px-2 py-1.5 border rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Depth (cm)</label>
                      <input
                        type="number"
                        value={editForm?.depth_cm || ''}
                        onChange={(e) => updateField('depth_cm', parseFloat(e.target.value) || 0)}
                        className="w-full px-2 py-1.5 border rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Height (cm)</label>
                      <input
                        type="number"
                        value={editForm?.height_cm || ''}
                        onChange={(e) => updateField('height_cm', parseFloat(e.target.value) || 0)}
                        className="w-full px-2 py-1.5 border rounded text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Weight */}
                <div>
                  <label className="text-xs text-gray-500">Weight (kg)</label>
                  <input
                    type="number"
                    value={editForm?.weight_kg || ''}
                    onChange={(e) => updateField('weight_kg', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>

                {/* Dropdowns */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500">Attribute</label>
                    <select
                      value={editForm?.attribute || 'none'}
                      onChange={(e) => updateField('attribute', e.target.value)}
                      className="w-full px-2 py-1.5 border rounded text-sm"
                    >
                      {ATTRIBUTES.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Stacking</label>
                    <select
                      value={editForm?.stacking_class || 'C1'}
                      onChange={(e) => updateField('stacking_class', e.target.value)}
                      className="w-full px-2 py-1.5 border rounded text-sm"
                    >
                      {STACKING_CLASSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500">Pallet Type</label>
                    <select
                      value={editForm?.pallet_type || 'EUR'}
                      onChange={(e) => updateField('pallet_type', e.target.value)}
                      className="w-full px-2 py-1.5 border rounded text-sm"
                    >
                      {PALLET_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Condition</label>
                    <select
                      value={editForm?.condition || 'good'}
                      onChange={(e) => updateField('condition', e.target.value)}
                      className="w-full px-2 py-1.5 border rounded text-sm"
                    >
                      {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                {/* Checkboxes */}
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={editForm?.notToploadable || false}
                      onChange={(e) => updateField('notToploadable', e.target.checked)}
                      className="rounded"
                    />
                    Not stackable
                  </label>
                </div>
              </div>
            ) : (
              // View mode
              <div
                className="flex items-stretch cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => startEdit(pallet)}
              >
                {/* Thumbnails - show both label and pallet images if available */}
                <div className="flex flex-shrink-0">
                  {/* Label image */}
                  {pallet.labelImageUrl ? (
                    <div className="w-16 h-20 bg-blue-50 relative border-r border-gray-200">
                      <img
                        src={pallet.labelImageUrl}
                        alt="Label"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-blue-600/80 text-white text-[10px] text-center py-0.5">
                        <Tag size={8} className="inline mr-0.5" />Label
                      </div>
                    </div>
                  ) : null}
                  {/* Pallet image */}
                  {pallet.imageUrl ? (
                    <div className="w-16 h-20 bg-orange-50 relative">
                      <img
                        src={pallet.imageUrl}
                        alt="Pallet"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-orange-600/80 text-white text-[10px] text-center py-0.5">
                        <Package size={8} className="inline mr-0.5" />Pallet
                      </div>
                    </div>
                  ) : !pallet.labelImageUrl ? (
                    <div className="w-20 h-20 bg-gray-100 flex items-center justify-center">
                      <ImageIcon className="w-6 h-6 text-gray-300" />
                    </div>
                  ) : null}
                  {/* Scan status indicator */}
                  {pallet.labelScanned && !pallet.palletScanned && (
                    <div className="absolute top-1 right-1 bg-yellow-500 text-white text-[8px] px-1 rounded">
                      AI est.
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 p-3 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <h4 className="font-medium text-sm truncate">{pallet.name}</h4>
                      {pallet.shipment_id && (
                        <p className="text-xs text-blue-600 flex items-center gap-1">
                          <Tag size={10} /> {pallet.shipment_id}
                        </p>
                      )}
                      {pallet.inferred_goods_type && (
                        <p className="text-xs text-yellow-700 bg-yellow-50 px-1 rounded inline-block">
                          🔍 {pallet.inferred_goods_type}
                        </p>
                      )}
                      {pallet.destination && (
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <MapPin size={10} /> {pallet.destination}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-0.5">
                        {pallet.width_cm}×{pallet.depth_cm}×{pallet.height_cm} cm · {pallet.weight_kg} kg
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(pallet.id);
                      }}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 -mr-1"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  
                  {/* Tags */}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {pallet.attribute !== 'none' && (
                      <span className={cn(
                        "px-1.5 py-0.5 rounded text-xs font-medium",
                        pallet.attribute === 'dangerous' && "bg-red-100 text-red-700",
                        pallet.attribute === 'food' && "bg-green-100 text-green-700",
                        pallet.attribute === 'medicine' && "bg-blue-100 text-blue-700",
                      )}>
                        {pallet.attribute}
                      </span>
                    )}
                    <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                      {pallet.pallet_type}
                    </span>
                    <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                      {pallet.stacking_class}
                    </span>
                    {pallet.notToploadable && (
                      <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">
                        No stack
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT TAB - Generate Load Planner JSON
// ─────────────────────────────────────────────────────────────────────────────
function ExportTab({ pallets }) {
  const [copied, setCopied] = useState(false);
  const [loadName, setLoadName] = useState(`Scan ${new Date().toLocaleDateString()}`);

  // Convert pallets to solver-compatible format (dimensions in meters)
  const exportData = {
    name: loadName,
    created: new Date().toISOString(),
    palletCount: pallets.length,
    pallets: pallets.map((p, index) => ({
      id: index + 1,
      name: p.name,
      // Shipping info from label scan
      shipment_id: p.shipment_id || null,
      destination: p.destination || null,
      sender: p.sender || null,
      receiver: p.receiver || null,
      // Dimensions (converted to meters for solver)
      dimensions: {
        width: p.width_cm / 100,  // Convert cm to meters
        depth: p.depth_cm / 100,
        height: p.height_cm / 100,
      },
      weight: p.weight_kg,
      attribute: p.attribute,
      notToploadable: p.notToploadable,
      // Extended fields
      stacking_class: p.stacking_class,
      pallet_type: p.pallet_type,
      condition: p.condition,
      orientation_preference: p.orientation_preference,
      // Scan metadata
      labelScanned: p.labelScanned || false,
      palletScanned: p.palletScanned || false,
    })),
  };

  const jsonString = JSON.stringify(exportData, null, 2);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pallet-scan-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (pallets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="p-4 bg-gray-100 rounded-full mb-4">
          <Download className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-700 mb-2">Nothing to export</h3>
        <p className="text-sm text-gray-500 text-center">
          Scan some pallets first in the Capture tab
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      {/* Load Name Input */}
      <div>
        <label className="text-sm font-medium text-gray-700 mb-1 block">Load Name</label>
        <input
          type="text"
          value={loadName}
          onChange={(e) => setLoadName(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg text-sm"
          placeholder="Enter a name for this load"
        />
      </div>

      {/* Summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Export Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Total pallets</span>
            <span className="font-medium">{pallets.length}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Total weight</span>
            <span className="font-medium">
              {pallets.reduce((sum, p) => sum + (p.weight_kg || 0), 0).toLocaleString()} kg
            </span>
          </div>
          {/* Unique destinations */}
          {(() => {
            const destinations = [...new Set(pallets.map(p => p.destination).filter(Boolean))];
            return destinations.length > 0 ? (
              <div className="flex justify-between">
                <span className="text-gray-500">Destinations</span>
                <span className="font-medium text-right text-xs max-w-[60%] truncate" title={destinations.join(', ')}>
                  {destinations.slice(0, 3).join(', ')}{destinations.length > 3 ? ` +${destinations.length - 3}` : ''}
                </span>
              </div>
            ) : null;
          })()}
          <div className="flex justify-between">
            <span className="text-gray-500">Dangerous goods</span>
            <span className="font-medium">
              {pallets.filter(p => p.attribute === 'dangerous').length}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Non-stackable</span>
            <span className="font-medium">
              {pallets.filter(p => p.notToploadable).length}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Labels scanned</span>
            <span className="font-medium">
              {pallets.filter(p => p.labelScanned).length}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          onClick={handleDownload}
          className="flex-1 h-12 gap-2"
        >
          <Download size={18} />
          Download JSON
        </Button>
        <Button
          onClick={handleCopy}
          variant="outline"
          className="flex-1 h-12 gap-2"
        >
          {copied ? <Check size={18} /> : <ClipboardList size={18} />}
          {copied ? 'Copied!' : 'Copy JSON'}
        </Button>
      </div>

      {/* JSON Preview */}
      <div>
        <label className="text-sm font-medium text-gray-700 mb-1 block">JSON Preview</label>
        <pre className="p-3 bg-gray-900 text-gray-100 rounded-lg text-xs overflow-x-auto max-h-64 overflow-y-auto">
          {jsonString}
        </pre>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────

// Convert file to base64
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Upload image to Supabase storage and get a public URL
// Returns null if Supabase is disabled or user not logged in (caller should use base64 fallback)
async function uploadImageToStorage(file) {
  if (!SUPABASE_ENABLED) {
    console.log('[PalletScanner] Supabase disabled, skipping storage upload');
    return null;
  }
  
  // Check if user is authenticated (required by storage policies)
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    console.log('[PalletScanner] User not authenticated, skipping storage upload');
    return null;
  }
  
  const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  // Use user-prefixed path as required by storage policies
  const filename = `users/${user.id}/scanner/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
  
  console.log('[PalletScanner] Uploading to storage:', filename);
  
  const { error: uploadError } = await supabase.storage
    .from('pallet-images')
    .upload(filename, file, { upsert: false });
  
  if (uploadError) {
    console.error('[PalletScanner] Storage upload error:', uploadError);
    throw uploadError;
  }
  
  // Use signed URL (works with private buckets) - valid for 1 year
  const { data: signedData, error: signError } = await supabase.storage
    .from('pallet-images')
    .createSignedUrl(filename, 60 * 60 * 24 * 365);
  
  if (signError || !signedData?.signedUrl) {
    console.error('[PalletScanner] Signed URL error:', signError);
    // Fall back to public URL
    const { data } = supabase.storage.from('pallet-images').getPublicUrl(filename);
    return data.publicUrl;
  }
  
  console.log('[PalletScanner] Storage signed URL:', signedData.signedUrl.substring(0, 80));
  return signedData.signedUrl;
}

// Call backend to classify LABEL image with OpenAI Vision
// Extracts shipping info: ID, destination, weight, sender, receiver, etc.
async function classifyLabelImage(base64Image, imageUrl = null) {
  try {
    const apiBase = process.env.REACT_APP_API_BASE_URL_DEV || '';
    console.log('[classifyLabelImage] Sending request to:', `${apiBase}/api/classify-label-scanner`);
    console.log('[classifyLabelImage] Image size:', base64Image?.length, 'chars');
    
    const response = await fetch(`${apiBase}/api/classify-label-scanner`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        image: base64Image,
        imageUrl,
      }),
    });

    console.log('[classifyLabelImage] Response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[classifyLabelImage] Error response:', errorText);
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    console.log('[classifyLabelImage] Full API response:', JSON.stringify(data, null, 2));
    console.log('[classifyLabelImage] _mock:', data._mock, '_reason:', data._reason);
    return data.pallet || data;
  } catch (err) {
    console.error('Label classification error:', err);
    // Return mock data with AI assumptions for development/fallback
    const mockId = `SHP-${Math.floor(10000 + Math.random() * 90000)}`;
    const mockDestinations = ['Stockholm', 'Gothenburg', 'Malmö', 'Uppsala', 'Linköping'];
    const mockCompanies = ['Volvo AB', 'IKEA', 'H&M', 'Scania', 'Ericsson', 'SKF'];
    
    return {
      id: Date.now(),
      name: `Shipment ${mockId}`,
      shipment_id: mockId,
      destination: mockDestinations[Math.floor(Math.random() * mockDestinations.length)],
      sender: mockCompanies[Math.floor(Math.random() * mockCompanies.length)],
      receiver: mockCompanies[Math.floor(Math.random() * mockCompanies.length)],
      // AI assumptions for dimensions (common EUR pallet)
      width_cm: 80,
      depth_cm: 120,
      height_cm: Math.floor(100 + Math.random() * 60),
      weight_kg: Math.floor(300 + Math.random() * 500),
      attribute: 'none',
      stacking_class: 'C1',
      notToploadable: false,
      pallet_type: 'EUR',
      condition: 'good',
      orientation_preference: 'none',
      confidence: {
        dimensions: 0.4, // Lower confidence for assumed dimensions
        weight: 0.5,
        attribute: 0.6,
        label: 0.8,
      },
      scannedAt: new Date().toISOString(),
      _mock: true,
      _error: err.message,
    };
  }
}

// Call backend to classify pallet image with OpenAI Vision
// Uses Node.js backend API
async function classifyPalletImage(base64Image, imageUrl = null) {
  try {
    // Call Node.js backend API
    const apiBase = process.env.REACT_APP_API_BASE_URL_DEV || '';
    console.log('[PalletScanner] Calling classify-pallet-scanner API...');
    
    const response = await fetch(`${apiBase}/api/classify-pallet-scanner`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        image: base64Image,
        imageUrl,
      }),
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    console.log('[PalletScanner] Classification result:', data);
    return data.pallet || data;
  } catch (err) {
    console.error('Classification error:', err);
    // Return mock data for development/fallback
    return {
      id: Date.now(),
      name: `Pallet ${Date.now()}`,
      width_cm: 80,
      depth_cm: 120,
      height_cm: Math.floor(80 + Math.random() * 80),
      weight_kg: Math.floor(200 + Math.random() * 600),
      attribute: 'none',
      stacking_class: 'C1',
      notToploadable: false,
      pallet_type: 'EUR',
      condition: 'good',
      orientation_preference: 'none',
      confidence: {
        dimensions: 0.7,
        weight: 0.4,
        attribute: 0.8,
      },
      scannedAt: new Date().toISOString(),
      _mock: true,
      _error: err.message,
    };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function PalletScannerPage() {
  const [activeTab, setActiveTab] = useState('capture');
  const [pallets, setPallets] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingPallet, setPendingPallet] = useState(null); // Pallet with label scanned, awaiting pallet scan
  const [dbError, setDbError] = useState(null);

  // Load pallets from database on mount
  useEffect(() => {
    loadPalletsFromDb();
  }, []);

  // Load pallets from Supabase
  const loadPalletsFromDb = async () => {
    if (!SUPABASE_ENABLED) {
      console.log('[PalletScanner] Supabase disabled, using local state only');
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('scanned_pallet')
        .select('*')
        .order('scanned_at', { ascending: false });

      if (error) {
        console.error('[PalletScanner] Error loading pallets:', error);
        setDbError(error.message);
      } else if (data) {
        console.log('[PalletScanner] Loaded pallets from DB:', data.length);
        if (data.length > 0) {
          console.log('[PalletScanner] First pallet image_url:', data[0].image_url?.substring(0, 80));
        }
        // Map DB fields to frontend format
        const mapped = data.map(row => ({
          id: row.id,
          uuid: row.uuid,
          name: row.name,
          shipment_id: row.shipment_id,
          width_cm: row.width_cm,
          depth_cm: row.depth_cm,
          height_cm: row.height_cm,
          weight_kg: row.weight_kg,
          attribute: row.attribute,
          pallet_type: row.pallet_type,
          stacking_class: row.stacking_class,
          condition: row.condition,
          notToploadable: row.not_toploadable,
          orientation_preference: row.orientation_preference,
          destination: row.destination,
          sender: row.sender,
          receiver: row.receiver,
          imageUrl: row.image_url,
          labelImageUrl: row.label_image_url,
          labelScanned: row.label_scanned,
          palletScanned: row.pallet_scanned,
          confidence: row.confidence,
          scannedAt: row.scanned_at,
        }));
        setPallets(mapped);
      }
    } catch (err) {
      console.error('[PalletScanner] Error loading pallets:', err);
      setDbError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Save pallet to database
  const savePalletToDb = async (pallet) => {
    if (!SUPABASE_ENABLED) {
      console.log('[PalletScanner] Supabase disabled, saving to local state only');
      return pallet;
    }

    try {
      const row = {
        name: pallet.name,
        shipment_id: pallet.shipment_id,
        width_cm: pallet.width_cm,
        depth_cm: pallet.depth_cm,
        height_cm: pallet.height_cm,
        weight_kg: pallet.weight_kg,
        attribute: pallet.attribute || 'none',
        pallet_type: pallet.pallet_type || 'EUR',
        stacking_class: pallet.stacking_class || 'C1',
        condition: pallet.condition || 'good',
        not_toploadable: pallet.notToploadable || false,
        orientation_preference: pallet.orientation_preference || 'none',
        destination: pallet.destination,
        sender: pallet.sender,
        receiver: pallet.receiver,
        image_url: pallet.imageUrl,
        label_image_url: pallet.labelImageUrl,
        label_scanned: pallet.labelScanned || false,
        pallet_scanned: pallet.palletScanned || false,
        confidence: pallet.confidence,
        scanned_at: pallet.scannedAt || new Date().toISOString(),
      };

      console.log('[PalletScanner] Saving pallet to DB:', {
        name: row.name,
        image_url_length: row.image_url?.length || 0,
        image_url_preview: row.image_url?.substring(0, 60),
        label_image_url_length: row.label_image_url?.length || 0,
      });

      const { data, error } = await supabase
        .from('scanned_pallet')
        .insert([row])
        .select()
        .single();

      if (error) {
        console.error('[PalletScanner] Error saving pallet:', error);
        setDbError(error.message);
        return { ...pallet, _dbError: error.message };
      }

      // Return with DB id
      return {
        ...pallet,
        id: data.id,
        uuid: data.uuid,
      };
    } catch (err) {
      console.error('[PalletScanner] Error saving pallet:', err);
      return { ...pallet, _dbError: err.message };
    }
  };

  // Update pallet in database
  const updatePalletInDb = async (pallet) => {
    if (!SUPABASE_ENABLED || !pallet.id) return;

    try {
      const { error } = await supabase
        .from('scanned_pallet')
        .update({
          name: pallet.name,
          shipment_id: pallet.shipment_id,
          width_cm: pallet.width_cm,
          depth_cm: pallet.depth_cm,
          height_cm: pallet.height_cm,
          weight_kg: pallet.weight_kg,
          attribute: pallet.attribute,
          pallet_type: pallet.pallet_type,
          stacking_class: pallet.stacking_class,
          condition: pallet.condition,
          not_toploadable: pallet.notToploadable,
          orientation_preference: pallet.orientation_preference,
          destination: pallet.destination,
          sender: pallet.sender,
          receiver: pallet.receiver,
        })
        .eq('id', pallet.id);

      if (error) {
        console.error('[PalletScanner] Error updating pallet:', error);
      }
    } catch (err) {
      console.error('[PalletScanner] Error updating pallet:', err);
    }
  };

  // Delete pallet from database
  const deletePalletFromDb = async (id) => {
    if (!SUPABASE_ENABLED) return;

    try {
      const { error } = await supabase
        .from('scanned_pallet')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('[PalletScanner] Error deleting pallet:', error);
      }
    } catch (err) {
      console.error('[PalletScanner] Error deleting pallet:', err);
    }
  };

  const handlePalletScanned = async (palletData) => {
    const tempId = Date.now(); // Temporary ID before DB save
    const newPallet = palletData
      ? { ...createDefaultPallet(tempId), ...palletData, id: tempId }
      : createDefaultPallet(tempId);
    
    // Add to local state immediately
    setPallets(prev => [...prev, newPallet]);
    
    // Save to database
    const savedPallet = await savePalletToDb(newPallet);
    
    // Update with DB ID
    if (savedPallet.id !== tempId) {
      setPallets(prev => prev.map(p => p.id === tempId ? savedPallet : p));
    }
    
    // Switch to review tab after scanning
    setActiveTab('review');
  };

  // Handle batch scan completion
  const handleBatchComplete = async (batchResults) => {
    const newPallets = [];
    
    for (const palletData of batchResults) {
      const tempId = Date.now() + Math.random(); // Unique temporary ID
      const newPallet = { ...createDefaultPallet(tempId), ...palletData, id: tempId };
      newPallets.push(newPallet);
    }
    
    // Add all to local state
    setPallets(prev => [...prev, ...newPallets]);
    
    // Save all to database (in sequence to avoid issues)
    for (let i = 0; i < newPallets.length; i++) {
      const pallet = newPallets[i];
      const savedPallet = await savePalletToDb(pallet);
      
      // Update with DB ID
      if (savedPallet.id !== pallet.id) {
        setPallets(prev => prev.map(p => p.id === pallet.id ? savedPallet : p));
      }
    }
    
    // Switch to review tab after batch processing
    setActiveTab('review');
  };

  const handleUpdatePallet = async (updatedPallet) => {
    setPallets(prev =>
      prev.map(p => p.id === updatedPallet.id ? updatedPallet : p)
    );
    await updatePalletInDb(updatedPallet);
  };

  const handleDeletePallet = async (id) => {
    setPallets(prev => prev.filter(p => p.id !== id));
    await deletePalletFromDb(id);
  };

  const handleClearAll = async () => {
    // Delete all from DB
    if (SUPABASE_ENABLED) {
      for (const pallet of pallets) {
        await deletePalletFromDb(pallet.id);
      }
    }
    setPallets([]);
    setPendingPallet(null);
    setActiveTab('capture');
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-gray-500">Loading pallets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col max-w-full overflow-x-hidden">
      {/* Database error banner */}
      {dbError && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 flex items-center gap-2 text-yellow-800 text-sm">
          <AlertCircle size={16} />
          <span>Database: {dbError}</span>
          <button onClick={() => setDbError(null)} className="ml-auto text-yellow-600 hover:text-yellow-800">
            <X size={14} />
          </button>
        </div>
      )}
      
      {/* Supabase disabled info */}
      {!SUPABASE_ENABLED && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center gap-2 text-blue-800 text-sm">
          <AlertCircle size={16} />
          <span>Demo mode: Data stored locally only (refresh will clear)</span>
        </div>
      )}
      
      {/* Header */}
      <header className="bg-white border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Mobile sidebar trigger */}
          <SidebarTrigger className="md:hidden" />
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Pallet Scanner</h1>
            <p className="text-xs text-gray-500 hidden sm:block">Scan labels & pallets for load planning</p>
          </div>
        </div>
        {pallets.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
          >
            <RotateCcw size={14} className="mr-1" />
            <span className="hidden sm:inline">Clear</span>
          </Button>
        )}
      </header>

      {/* Tab Navigation */}
      <nav className="bg-white border-b px-2 flex">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          const count = tab.id === 'review' ? pallets.length : null;
          
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium border-b-2 transition-colors",
                isActive
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              )}
            >
              <Icon size={18} />
              <span>{tab.label}</span>
              {count !== null && count > 0 && (
                <span className={cn(
                  "ml-1 px-1.5 py-0.5 rounded-full text-xs",
                  isActive ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"
                )}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Tab Content */}
      <main className="flex-1 overflow-y-auto">
        {activeTab === 'capture' && (
          <CaptureTab
            onPalletScanned={handlePalletScanned}
            isProcessing={isProcessing}
            setIsProcessing={setIsProcessing}
            pendingPallet={pendingPallet}
            setPendingPallet={setPendingPallet}
          />
        )}
        {activeTab === 'batch' && (
          <BatchScanTab
            onBatchComplete={handleBatchComplete}
            isProcessing={isProcessing}
            setIsProcessing={setIsProcessing}
          />
        )}
        {activeTab === 'gallery' && (
          <GalleryTab
            pallets={pallets}
            onSelect={(pallet) => {
              // Navigate to review tab with this pallet selected
              setActiveTab('review');
            }}
            onDelete={handleDeletePallet}
          />
        )}
        {activeTab === 'review' && (
          <ReviewTab
            pallets={pallets}
            onUpdate={handleUpdatePallet}
            onDelete={handleDeletePallet}
          />
        )}
        {activeTab === 'export' && (
          <ExportTab pallets={pallets} />
        )}
      </main>

      {/* Bottom action bar for mobile */}
      {activeTab === 'review' && pallets.length > 0 && (
        <div className="bg-white border-t p-3 flex gap-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => setActiveTab('capture')}
          >
            <Plus size={16} className="mr-1" />
            Add More
          </Button>
          <Button
            className="flex-1"
            onClick={() => setActiveTab('export')}
          >
            <Download size={16} className="mr-1" />
            Export ({pallets.length})
          </Button>
        </div>
      )}
    </div>
  );
}
